import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";

export default function NewsDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/news/${slug}`).then((r) => setPost(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="aoa-container py-32 text-center text-slate-500">News post not found.</div>;
  if (!post) return <div className="aoa-container py-32 text-center text-slate-400">Loading…</div>;

  // body_html may contain markdown (new editor) or HTML (legacy). If it starts with
  // an HTML tag we render it via dangerouslySetInnerHTML; otherwise we render via
  // sanitized markdown.
  const body = post.body_html || "";
  const looksLikeHTML = /^\s*<[a-z!][^>]*>/i.test(body);

  return (
    <>
      <SEO
        title={post.meta_title || post.title}
        description={post.meta_description || post.excerpt || ""}
        ogImage={post.cover_image || "/imagery/airliner.png"}
        path={`/news/${post.slug}`}
        type="article"
      />
      <article className="bg-white">
        <div className="aoa-container py-16 max-w-4xl mx-auto" data-testid={`news-detail-${post.slug}`}>
          <Link to="/news" className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> All news
          </Link>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Calendar className="h-3.5 w-3.5" />
            {post.published_at ? new Date(post.published_at).toLocaleDateString() : ""}
          </div>
          <h1 className="aoa-h1 mt-3 text-[hsl(var(--aoa-navy))]">{post.title}</h1>
          {post.excerpt && <p className="mt-4 text-lg text-slate-600 leading-relaxed">{post.excerpt}</p>}
          {post.cover_image && (
            <img
              src={post.cover_image}
              alt=""
              width={1280}
              height={720}
              loading="lazy"
              decoding="async"
              className="mt-8 w-full rounded-2xl aspect-[16/9] object-cover border border-slate-200 bg-[#F1F5F9]"
            />
          )}
          {body && (
            <div className="mt-10 prose prose-slate max-w-none text-[15px] leading-relaxed prose-headings:font-display prose-headings:text-[hsl(var(--aoa-navy))] prose-a:text-[hsl(var(--aoa-royal))]">
              {looksLikeHTML ? (
                <div dangerouslySetInnerHTML={{ __html: body }} />
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                  {body}
                </ReactMarkdown>
              )}
            </div>
          )}
        </div>
      </article>
    </>
  );
}
